import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  TextField,
  Button,
  Avatar,
} from "@mui/material";
import {
  Business,
  Email,
  Home,
  Save,
  Description,
  School,
} from "@mui/icons-material";
import { useSelector } from "react-redux";

import OverlayLoader from "../../../../components/OverlayLoader";
import HttpClient from "../../../../helpers/HttpClient";
import { showToast } from "../../../../utils/toast";

export default function DistributorCreatePage() {
  const [openLoader, setOpenLoader] = useState(false);

  const authState = useSelector((state) => state.auth);
  const userId = authState.user ? authState.user.email : "test";

  const [cursosDisponibles, setCursosDisponibles] = useState([]);

  const [formData, setFormData] = useState({
    nombre: "",
    correo: "",
    direccion: "",
  });

  const [errors, setErrors] = useState({
    nombre: "",
    correo: "",
    direccion: "",
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Validaciones básicas
    if (field === "nombre" && value.length > 50) {
      setErrors((prev) => ({
        ...prev,
        nombre: "El nombre no puede superar los 50 caracteres.",
      }));
    } else if (field === "direccion" && value.length > 100) {
      setErrors((prev) => ({
        ...prev,
        direccion: "La dirección no puede superar los 100 caracteres.",
      }));
    } else if (field === "correo") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && !emailRegex.test(value)) {
        setErrors((prev) => ({
          ...prev,
          correo: "Por favor ingrese un correo electrónico válido.",
        }));
      } else {
        setErrors((prev) => ({ ...prev, correo: "" }));
      }
    } else {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const reset = () => {
    setFormData({
      nombre: "",
      correo: "",
      direccion: "",
    });
    setErrors({
      nombre: "",
      correo: "",
      direccion: "",
    });
  };

  const isFormValid = () => {
    const { nombre, correo, direccion } = formData;
    return (
      nombre.trim() &&
      correo.trim() &&
      direccion.trim() &&
      Object.values(errors).every((e) => e === "")
    );
  };

  const handleGuardar = async () => {
    if (!isFormValid()) {
      showToast(
        "Por favor completa todos los campos correctamente.",
        "warning"
      );
      return;
    }

    setOpenLoader(true);
    const result = await HttpClient().post(`/admin/distributor/create`, {
      name: formData.nombre,
      email: formData.correo,
      address: formData.direccion,
      file_name: `${formData.ficheroConstancia}.pdf`,
      user: userId,
      courses: formData.constanciasCursos, // 👈 agregamos los cursos seleccionados
    });

    setOpenLoader(false);
    if (result.error) {
      showToast(
        "No se pudo crear el distribuidor. Intenta nuevamente.",
        "error"
      );
      return;
    }

    if (result.status === 201) {
      showToast(
        "Distribuidor creado correctamente con ID " + result.data.id,
        "success"
      );
      reset();
    } else {
      showToast(
        "No se pudo crear el distribuidor. Intenta nuevamente.",
        "warning"
      );
    }
  };

  return (
    <Box sx={{ backgroundColor: "#F5F5F5", minHeight: "100vh", p: 2 }}>
      <OverlayLoader open={openLoader} />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          color="#374151"
          sx={{ fontSize: "1.3rem" }}
        >
          Crear nuevo distribuidor/empresa
        </Typography>

      </Box>

      {/* Formulario */}
      <Card
        sx={{
          borderRadius: 2,
          border: "1px solid #E5E7EB",
          backgroundColor: "white",
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={3}>
            {/* Nombre */}
            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Business sx={{ color: "#6B7280", fontSize: 16 }} />
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ fontSize: "0.85rem" }}
                >
                  Nombre del distribuidor:
                </Typography>
              </Box>
              <TextField
                fullWidth
                size="small"
                value={formData.nombre}
                onChange={(e) => handleChange("nombre", e.target.value)}
                placeholder="Ingrese el nombre del distribuidor"
                error={!!errors.nombre}
                helperText={errors.nombre || "Máximo 50 caracteres"}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#F9FAFB",
                    fontSize: "0.8rem",
                  },
                }}
              />
            </Grid>

            {/* Correo */}
            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Email sx={{ color: "#6B7280", fontSize: 16 }} />
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ fontSize: "0.85rem" }}
                >
                  Correo electrónico:
                </Typography>
              </Box>
              <TextField
                fullWidth
                size="small"
                type="email"
                value={formData.correo}
                onChange={(e) => handleChange("correo", e.target.value)}
                placeholder="Ingrese el correo del distribuidor"
                error={!!errors.correo}
                helperText={errors.correo || "Debe ser un correo válido"}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#F9FAFB",
                    fontSize: "0.8rem",
                  },
                }}
              />
            </Grid>

            {/* Dirección */}
            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Home sx={{ color: "#6B7280", fontSize: 16 }} />
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ fontSize: "0.85rem" }}
                >
                  Dirección:
                </Typography>
              </Box>
              <TextField
                fullWidth
                size="small"
                value={formData.direccion}
                onChange={(e) => handleChange("direccion", e.target.value)}
                placeholder="Ingrese la dirección del distribuidor"
                error={!!errors.direccion}
                helperText={errors.direccion || "Máximo 100 caracteres"}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#F9FAFB",
                    fontSize: "0.8rem",
                  },
                }}
              />
            </Grid>  
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Botón Guardar */}
          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <Button
              variant="contained"
              startIcon={<Save />}
              size="medium"
              onClick={handleGuardar}
              disabled={!isFormValid()}
              sx={{
                backgroundColor: "#10B981",
                color: "white",
                fontWeight: "bold",
                px: 3,
                py: 1,
                borderRadius: 2,
                fontSize: "0.8rem",
                "&:hover": { backgroundColor: "#059669" },
                "&:disabled": {
                  backgroundColor: "#D1D5DB",
                  color: "#9CA3AF",
                },
              }}
            >
              Guardar distribuidor
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
